/**
 * Closing a blocking dialog with the barcode scanner.
 *
 * Owner, 2026-09-23: "لو شغال بسكانر من بعيد يضغط 2 تكة بيه تتقفل ويكمل" — on the scanning screens the operator is
 * usually across the room with a parcel in one hand and the scanner in the other, nowhere near the keyboard. Two
 * shots of the scanner close whatever is in his way and hand the keyboard back to that screen's scan box.
 *
 * The FIRST shot is deliberately swallowed and never reaches the screen behind. On a refusal the parcel being
 * refused is still the one under the scanner, so letting that shot through would scan in the very thing the dialog
 * is there to stop. The first arms, the second closes, and the hint says so.
 *
 * Used by every screen that scans — see the callers of `useScanToClose`.
 */
import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import { useT } from '@/i18n'

export const SCAN_TO_CLOSE = {
  /** Both shots must land inside this window, or the count starts again. */
  windowMs: 1500,
  /**
   * Scanners often fire the same barcode twice on one pull; that is one shot, not two. A pull's echo lands within
   * tens of ms — at 400 ms two quick deliberate pulls on the same parcel counted as one and nothing closed (2026-09-28).
   */
  dedupeMs: 90,
}

export interface ScanToClose {
  /** The first shot has landed: the next one closes. */
  armed: boolean
  /** What to tell the operator right now, in his language. */
  hint: string
  /** Shown once a shot has been swallowed, so he knows it was not scanned in. */
  swallowedNote: string
  /** Spread onto a visually hidden input INSIDE the dialog, so the focus trap keeps every shot in it. */
  captureProps: {
    ref: MutableRefObject<HTMLInputElement | null>
    type: 'text'
    className: string
    'aria-label': string
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  }
}

/**
 * `resetKey` restarts the count whenever the dialog's subject changes — a second refusal replacing the first must
 * not be closable by a shot that was aimed at the one before it.
 */
export function useScanToClose({ open, onClose, resetKey = '', onPass, holds = [] }: {
  open: boolean
  onClose: () => void
  resetKey?: string
  /**
   * A scan screen that can take the next parcel while the dialog is up: a shot whose code is NOT one of `holds` (the
   * parcels the dialog is about) is a new parcel, and goes to the screen instead of being lost (owner, 2026-09-28:
   * "لو الموظف ضرب شحنتين ورا بعض … مش بنكون عارفين الاتنين اتضربوا ولا لا"). It still counts toward closing.
   */
  onPass?: (code: string) => void
  holds?: string[]
}): ScanToClose {
  const { locale } = useT()
  const ar = locale === 'ar'
  const [armed, setArmed] = useState(false)
  const [passed, setPassed] = useState<string[]>([])
  const capture = useRef<HTMLInputElement | null>(null)
  const last = useRef<{ code: string; at: number } | null>(null)
  const firstAt = useRef(0)

  useEffect(() => {
    if (!open) return
    setArmed(false)
    setPassed([])
    last.current = null
    firstAt.current = 0
    const id = window.setTimeout(() => capture.current?.focus(), 30)
    return () => window.clearTimeout(id)
  }, [open, resetKey])

  const shot = (code: string) => {
    const held = holds.some((h) => h.toUpperCase() === code.toUpperCase())
    if (onPass && code && !held && !passed.includes(code.toUpperCase())) {
      onPass(code)
      setPassed((p) => [...p, code.toUpperCase()])
    }
    const now = Date.now()
    const prev = last.current
    last.current = { code, at: now }
    // the same barcode arriving twice in a blink is one pull of the trigger
    if (prev && prev.code === code && now - prev.at < SCAN_TO_CLOSE.dedupeMs) return
    if (armed && now - firstAt.current <= SCAN_TO_CLOSE.windowMs) { onClose(); return }
    setArmed(true)
    firstAt.current = now
  }

  return {
    armed,
    hint: armed
      ? (ar ? 'امسح مرة أخرى للإغلاق' : 'Scan again to close')
      : (ar ? 'هل تستخدم الماسح؟ امسح مرتين متتاليتين للإغلاق' : 'On a scanner? Two scans in a row close this'),
    swallowedNote: passed.length
      ? (ar ? `أضيفت إلى العملية: ${passed.join('، ')} — امسح مرة أخرى للإغلاق` : `Added to the operation: ${passed.join(', ')} — scan again to close`)
      : (ar ? 'لم يتم تسجيل هذا المسح — امسح مرة أخرى للإغلاق والمتابعة' : 'That scan was not added — scan again to close'),
    captureProps: {
      ref: capture,
      type: 'text',
      className: 'sr-only',
      'aria-label': ar ? 'امسح مرتين للإغلاق' : 'Scan twice to close',
      onKeyDown: (e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        const code = e.currentTarget.value.trim()
        e.currentTarget.value = ''
        shot(code)
      },
    },
  }
}

/** The hidden input plus the "that scan was not added" note — what a dialog drops in to get the behaviour. */
export function ScanToCloseCapture({ scan }: { scan: ScanToClose }) {
  return (
    <>
      {scan.armed && (
        <p className="rounded-md bg-warning-soft px-3 py-1.5 text-xs font-medium text-warning">{scan.swallowedNote}</p>
      )}
      <input {...scan.captureProps} />
    </>
  )
}
